import clone from 'clone'
import { diff } from 'deep-object-diff'
import { AppContextProps } from '../../hooks/useApp'
import { callAxios } from '../../hooks/useAxios'
import { Device, DevicePort, DeviceValues, ID, Port, PortFlag, PortStatus } from '../../types/models'
import { PortConfigDiff, PortValueName } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { SystemControl } from '../SystemControl'
import { t } from 'i18next'
import { ApplianceSubtype } from '../appliances/Appliance'
import { SolarPortControl } from './SolarPortControl'

/**
 * This is a base class defining interface for port control instances of different type.
 */
export class PortControl<PortType extends Port, PortConfig extends object> {

  port: PortType
  deviceControl: DeviceControl
  orderNumber: number
  state: Record<string, unknown>

  constructor(deviceControl: DeviceControl, port: Port) {
    this.deviceControl = deviceControl
    this.orderNumber = NaN
    this.port = port as PortType
    this.state = {}
  }

  get app(): AppContextProps {
    return this.deviceControl.app
  }

  get device(): Device {
    return this.deviceControl.device
  }

  get values(): Partial<Record<PortValueName, unknown>> {
    throw new Error('Do not access values directly.')
  }

  get config(): Record<string, unknown> {
    throw new Error('Do not access config directly.')
  }

  get systemControl(): SystemControl {
    return this.deviceControl.system
  }

  errorFlags(): PortFlag[] {
    return this.port.flags
  }

  /**
   * The error flags as translated, user-facing messages. Subclasses that know
   * more than the flag says (e.g. which address is being tried) override this.
   */
  errorMessages(): string[] {
    return this.errorFlags().map(flag => this.app.t(`Error-${flag}`))
  }

  /**
   * Check if this port has the given config.
   */
  hasConfig(variable: string): boolean {
    return (variable in this.getConfigDefaults())
  }

  /**
   * Check both config and config.pending for a value.
   * Pending values (set via setConfig) take priority over committed values.
   */
  getConfig<T = any>(name: string): T {
    const config: Record<string, unknown> = this.port.config as unknown as Record<string, unknown>

    if (config.pending) {
      const pending: Record<string, unknown> = config.pending as Record<string, unknown>

      if (pending[name] !== undefined) {
        return pending[name] as T
      }
    }
    return config[name] as T
  }

  /**
   * Obtain config.pending object.
   */
  getPendingConfig(): unknown {
    const config: Record<string, unknown> = this.port.config as unknown as Record<string, unknown>
    return config.pending
  }

  /**
   * Update port config and store the change to `pending` part of config.
   */
  setConfig(key: string, value: any, refresh: boolean = true): void {
    const config: Record<string, unknown> = this.port.config as unknown as Record<string, unknown>
    const pending = Object.assign(clone(config.pending as object) || {}, { [key]: value })
    config.pending = pending

    if (refresh) this.deviceControl.refresh()
  }

  /**
   * Remove a value from the config.
   */
  deleteConfig<P extends PortConfig>(key: keyof P, refresh: boolean = true): void {
    const config: Record<string, unknown> = this.port.config as unknown as Record<string, unknown>
    delete config[key as string]
    if (config.pending) {
      delete (config.pending as Record<string, unknown>)[key as string]
    }

    if (refresh) this.deviceControl.refresh()
  }

  /**
   * Get pending config changes.
   */
  uncommittedConfig(): Partial<PortConfig> | null {
    const pending = this.port.config.pending || {}
    return Object.keys(pending).length
      ? pending as Partial<PortConfig>
      : null
  }

  /**
   * Whether the device is known to run exactly the given flash config, so that
   * flashing it again would only repeat what the device already has (and, with
   * the confmit that follows every flash, reboot the device for nothing).
   *
   * Errs on the side of flashing. This is `true` only when
   * - the device has reported its config (`config.hardware` is an object; `null`
   *   is a failed read, `undefined` a device that has never reported it),
   * - nothing is waiting to be sent or acknowledged (`config.pending` is empty;
   *   the server keeps an unacknowledged setconf there while it retries it, and
   *   the UI keeps its own edits there until they are flashed), and
   * - every key the flash would carry is present in the reported config with an
   *   equal value.
   */
  isConfirmedOnDevice(flashConfig: Record<string, unknown>): boolean {
    const hardware = this.port.config.hardware as Record<string, unknown> | null | undefined
    if (hardware === undefined || hardware === null || typeof hardware !== 'object') {
      return false
    }
    if (this.uncommittedConfig() !== null) {
      return false
    }
    return Object.keys(flashConfig).every(key => {
      if (!(key in hardware)) return false
      const wanted = flashConfig[key]
      const reported = hardware[key]
      if (typeof wanted === 'object' && wanted !== null && typeof reported === 'object' && reported !== null) {
        return Object.keys(diff(wanted, reported)).length === 0
      }
      return wanted === reported
    })
  }

  /**
   * Collect all related ports for associating appliance to this port.
   */
  getAssociatedPortIDs(): ID[] {
    return [this.port.id as ID]
  }

  /**
   * Collect device IDs and port numbers for associated ports.
   */
  getAssociatedDevicePorts(): DevicePort[] {
    const ids = this.getAssociatedPortIDs()
    return ids.map(id => {
      const ctlr = this.systemControl.portControlForId(id)
      return { deviceId: ctlr.device.id as ID, portNumber: ctlr.port.port_number }
    })
  }

  getValue<T>(name: PortValueName): T {
    return (this.port.values as Record<string, unknown>)[name] as unknown as T
  }

  get title(): string {
    return this.getTypeName() || t(`PortType-${this.port.type}`)

  }

  set title(name: string) {
    throw new Error('Port title is obsolete.')
  }

  get group(): string {
    return this.app.t('PortGroup-Other')
  }

  get number(): number {
    return this.orderNumber
  }

  get status(): PortStatus {
    const hasEnableDisable = this.hasEnableDisable
    const isTurnedOff = hasEnableDisable && !this.getConfig('enabled')
    const hasErrors = !isTurnedOff && this.port.flags.length > 0

    return isTurnedOff ? 'none' : (hasErrors ? 'error' : 'ok')
  }

  getName(): string {
    return this.getTypeName()
  }

  getDescription(): string {
    throw new Error(`Port ${JSON.stringify(this.port)} does not implement getDescription().`)
  }

  getTypeName(): string {
    return this.app.t('PortType-Unknown Port')
  }

  getSubtype(): ApplianceSubtype {
    return this.port.type
  }

  gotoPort(id: ID): void {
    setTimeout(() => {
      document.getElementById(`port-config-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
    setTimeout(() => {
      document.getElementById(`port-config-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 400)
    setTimeout(() => {
      document.getElementById(`port-config-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 600)
  }

  /**
   * Check if this port is configured as a net sum port.
   */
  isSummer(): boolean {
    if (!this.getConfig('enabled')) {
      return false
    }
    if (this.hasConfig('to_summer')) {
      return !!this.getConfig('to_summer')
    }
    if (this.hasConfig('to_summer_inverted')) {
      return !this.getConfig('to_summer_inverted')
    }

    return false
  }

  /**
   * Send port values as an API update.
   */
  async sendCommand(values: Record<string, any> = {}): Promise<unknown> {
    const result = await callAxios({
      url: `/ports/${this.port.id}`,
      method: 'PUT',
    }, {
      values
    })

    if (!result.success) {
      this.app.addError(result)
    }

    return result
  }

  /**
   * Send new values for device.
   */
  async saveDeviceValues(values: DeviceValues): Promise<boolean> {
    const res = await callAxios({
      method: 'PATCH',
      url: `/devices/${this.device.id}`,
    }, { values: { ...this.device.values, ...values } })

    if (!res.success) {
      this.app.addError(res)
    }

    return res.success
  }

  /**
   * Get defaults for the hard configuration.
   */
  getConfigDefaults(): PortConfig {
    return {} as PortConfig
  }

  /**
   * Adjust the config right before it is flashed to the device, e.g. round
   * values to the wire unit so that the read-back equals what was sent.
   * Identity by default.
   */
  normalizeFlashConfig(config: Record<string, unknown>): Record<string, unknown> {
    return config
  }

  /**
   * Set defaults for the hard configuration.
   */
  setConfigDefaults(): void {
    const conf = this.getConfigDefaults()
    Object.keys(conf).forEach(k => this.setConfig(k, (conf as Record<string, unknown>)[k]))
  }

  /**
   * Full width informative widget on the top of configuration panel.
   */
  infoTop() : React.ReactNode | undefined {
    return undefined
  }

  /**
   * Informative widget for the left side of configuration panel.
   */
  infoLeft(): React.ReactNode | undefined {
    return undefined
  }

  /**
   * Informative widget for the right side of configuration panel.
   */
  infoRight(): React.ReactNode | undefined {
    return undefined
  }

  /**
   * Non-flashable configuration panel on the left.
   */
  configTopLeft() : React.ReactNode | undefined {
    return undefined
  }

  /**
   * Non-flashable configuration panel on the right.
   */
  configTopRight() : React.ReactNode | undefined {
    return undefined
  }

  /**
   * Flashable configuration panel on the left.
   */
  configBottomLeft() : React.ReactNode | undefined {
    return undefined
  }

  /**
   * Flashable configuration panel on the right.
   */
  configBottomRight() : React.ReactNode | undefined {
    return undefined
  }

  /**
   * Additional configuration panel on the bottom.
   */
  configBottom() : React.ReactNode | undefined {
    return undefined
  }

  // Feature flags.
  get hasEnableDisable() { return 'enabled' in this.getConfigDefaults() }
  get isVisible() { return true }
  get noDiff() { return false }

  /**
   * Config keys left out of the hardware-vs-settings comparison: protocol
   * bookkeeping the user neither sets nor can fix (e.g. wire-layout flags).
   */
  get diffIgnoredKeys(): string[] { return [] }

  /**
   * Construct description of differencies between set configuration and hardware reported configuration.
   */
  hardwareDiff(): PortConfigDiff {
    const ret: PortConfigDiff = {
      id: this.port.id as ID,
      name: this.getName(),
      type: 'OK',
    }

    if (!this.isVisible || this.noDiff) {
      return {
        ...ret,
        type: 'FUTURE',
      }
    }
    if (this.port.config.hardware === undefined) {
      return ret
    }
    if (this.port.config.hardware === null) {
      return {
        ...ret,
        type: 'READ FAILED',
      }
    }

    // Figure out facts and fill in missing info.
    const hardwareEnabled = this.hasEnableDisable && ('enabled' in this.port.config.hardware) && this.port.config.hardware.enabled
    const softwareEnabled = this.hasEnableDisable && ('enabled' in this.port.config) && this.port.config.enabled
    if (!hardwareEnabled && !softwareEnabled) {
      return {
        ...ret,
        type: 'DISABLED',
      }
    }

    // Helper to compare config values.
    const isDifferent = (key: string, a: unknown, b: unknown): boolean => {
      // Ignore if we don't have value from hardware.
      if (b === undefined) return false

      if (this.diffIgnoredKeys.includes(key)) return false

      // Some special cases.
      if (key === 'params') {
        const driver = (this as SolarPortControl).getConfig('driver')
        if (driver === 'SPP_SOLAR_DRIVER_RELAYED') {
          return false
        }
      }

      // Compare objects.
      if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
        return Object.keys(diff(a, b)).length > 0
      }
      // Accept empty subobjects that are not set.
      if (typeof a === 'object' && a !== null && Object.keys(a).length === 0 && !b) {
        return false
      }
      if (typeof b === 'object' && b !== null && Object.keys(b).length === 0 && !a) {
        return false
      }
      return a !== b
    }

    // Build list of differencies.
    let success = true
    const failures: PortConfigDiff = {
      id: this.port.id as ID,
      name: this.getName(),
      type: 'DIFFERS',
      hardware: {},
      config: {},
    }

    const softConf: Record<string, any> = { ...this.getConfigDefaults(), ...this.port.config }
    const hardConf: Record<string, any> = this.port.config.hardware || {}
    const variables: string[] = Object.keys({ ...hardConf, ...this.getConfigDefaults() })

    variables.forEach(key => {
      if (isDifferent(key, softConf[key], hardConf[key])) {
        success = false
        if (typeof softConf[key] === 'object' && softConf[key] !== null && !(softConf[key] as any instanceof Array)) {
          Object.keys(softConf[key]).forEach(s => {
            const hw = (key in hardConf) ? hardConf[key][s] : null
            const sw = (key in softConf) ? softConf[key][s] : null
            if (isDifferent(`${key}.${s}`, hw, sw)) {
              failures.hardware[`${key}.${s}`] = hw
              failures.config[`${key}.${s}`] = sw
            }
          })
        } else {
          failures.hardware[key] = hardConf[key]
          failures.config[key] = softConf[key]
        }
      }
    })

    // Check that hardware and config objects have values, otherwise there is no reported differences.
    if (Object.keys(failures.hardware).length === 0 && Object.keys(failures.config).length === 0) {
      success = true
    }

    if (!success) return failures

    return ret
  }

  /**
   * Save temporary values for this object and do not alter them after first save.
   */
  saveState<T>(name: string, value: T): T {
    if (!(name in this.state)) {
      this.state[name] = value
    }
    return this.state[name] as T
  }
}
